import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { StudioUtils, sort, sortObjectByKeys } from '@wms/core';
import { BaseComponent } from '@wms/components';
import { flatten, groupBy, isObject, map, sortBy, toLower, transform } from 'lodash-es';
import { TypeaheadContainerComponent, TypeaheadDirective, TypeaheadMatch, TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { CommonWidgetDirective } from "@wms/directives";
import { Observable, of } from 'rxjs';

@Component({
  selector: 'wms-typeahead',
  standalone: true,
  templateUrl: './wms-typeahead.component.html',
  styleUrls: ['./wms-typeahead.component.less'],
  imports: [
    CommonModule,
    TypeaheadModule,
    FormsModule,
    CommonWidgetDirective
  ],
  viewProviders: [{
    provide: ControlContainer,
    deps: [[Optional, NgForm]],
    useFactory: (ngForm: NgForm) => ngForm,
  }]
})
export class WmsTypeaheadComponent extends BaseComponent implements OnInit, OnChanges {

  @Input() name!: string;
  @Input() placeholder: string = "";
  @Input() disable: boolean = false;
  @Input() model!: any;
  @Input() options!: any;
  @Input() appendToBody: boolean = false;
  @Input() propertyName!: any;
  @Input() groupBy!: any;
  @Input() onFocus!: any;
  @Input() dropDirection!: any;
  @Input() caseSensitive: boolean = false;
  @Input() editable?: boolean = false;
  @Input() dropdownClass: string;
  @Input() showActionButtons: boolean = true;
  @Input() modelType: string = 'object';
  @Input() ngClass: any;
  @Input() modelKey!: string; // use this key to send different object key as selection value
  @Input() typeaheadUid!: string;
  @Input() clearOnSelect?: boolean
  @Input() updateValueOn?: any = "change";
  @Input() createNewLabel : string = "";
  @Input() disableSorting: boolean = false;
  @Input() itemTrailingIconClass: string = "";

  
  @Output() onCreateNew = new EventEmitter<string>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() onClear = new EventEmitter<any>();
  @Output() onTrailingIconClick = new EventEmitter<string>();
  isOptionsTransformed = false;
  transformedOptions: any = [];
  filteredOptions$: Observable<any[]> = of([]);
  typeaheadModel: any = "";
  tempTypeaheadModel: any = "";
  public typeaheadContainer: TypeaheadContainerComponent | undefined;
  @ViewChild('typeaheadInput') inputRef?: ElementRef<HTMLElement>;
  @ViewChild('expandBtn') expandBtnRef?: ElementRef<HTMLElement>;
  @ViewChild('clearBtn') clearBtnRef?: ElementRef<HTMLElement>;
  @ViewChild(TypeaheadDirective) typeahead: TypeaheadDirective;
  selectedOption: any;
  startedEditing: boolean = false;

  constructor(@Optional() private ngForm: NgForm) {
    super();
  }

  getFormControl() {
    if (this.ngForm && this.ngForm.form && this.name) {
      return this.ngForm.form.get(this.name);
    }
    return null;
  }

  ngOnInit(): void {
    this.selectedOption = this.model;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model']?.previousValue !== this.model || !this.editable) {
      this.selectedOption = this.model;
      if (!Array.isArray(this.options) && this.options && typeof (this.options[this.model]) === 'object') {
        this.typeaheadModel = this.options[this.model][this.propertyName];
      } else {
        this.typeaheadModel = typeof (this.model) === 'string' ? this.model : this.model && this.model[this.propertyName];
      }
    }
      this.isOptionsTransformed = false;
      const searchValue = this.isModelSameAsSelectedOption() || !this.typeaheadModel ? null : this.model;
      this.updateFilteredOptions(searchValue);
  }

  isModelSameAsSelectedOption() {
    let currentSelectedValue = isObject(this.selectedOption) ? (this.selectedOption as any)[this.propertyName] : this.selectedOption;
    return currentSelectedValue === this.typeaheadModel;
  }

  getSortedOptions(options: any) {
    if (this.disableSorting) {
      return options;
    }
    return this.groupBy ? flatten(map(sortObjectByKeys(groupBy(options, this.groupBy)), (g: any) => {
      g = sortBy(g, (i) => {
        return toLower(i.name);
      });
      g[0].firstInGroup = true;
      return g;
    })) : options;
  }

  typeaheadFocus(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggleFocus(true);
    this.updateFilteredOptions('');
  }

  updateFilteredOptions(searchValue: any = '') {
    const result = this.getSortedOptions(this.getOptions(this.options, searchValue));
    this.filteredOptions$ = of(result);
  }

  // This function transform the options to some standard structure where all the options are array of objects
  transformOptionsList(options: any): any[] {
    /* converting the options into array format from object and maps and preserve the key onto the value object
     which later might be required to sent back to the callback of on-select */
    if (options && Array.isArray(options)) {
      options = [...options].map((val: any, key: any) => {
        // if val is an object then add $key to val, else create val as a object having two properties $key and $value
        if (!val) {
          return;
        }
        if (isObject(val)) {
          Object.assign(val, { key });
        } else {
          val = { 'value': val, 'key': key };
        }
        /* if property name is not set then set the default value of property-name to $value which will be used when we have to
           sort the options
         */
        if (val.value && !this.propertyName) {
          this.propertyName = 'value';
        }
        if (this.groupBy) {
          val.groupedBy = val[this.groupBy];
        }
        return val;
      });
    }
    if (Array.isArray(options))
      options = options.filter((item: any) => item);
    return this.getSortedOptions(options);
  }

  onTouched() {
    let formControl = this.getFormControl();
    if (formControl) {
      formControl.markAsTouched();
      formControl.markAsDirty();
      formControl.updateValueAndValidity();
    }
  }

  handleOnSelect(event: TypeaheadMatch) {
    this.typeaheadModel = isObject(event.item) ? (event.item as any)[this.propertyName] : event.item;
    this.toggleFocus(false);
    this.model = event.item;
    this.startedEditing = false;
    this.onTouched(); // Mark control as touched
    this.onSelect.emit(this.modelType === 'string' && typeof event.item === 'object' ? this.modelKey ? (event.item as any)[this.modelKey] : (event.item as any)[this.propertyName] : event.item);
    this.clearOnSelect && this.handleClear()
  }

  onModelChange(value: any) {
    if (value === null || value === "") {
      this.tempTypeaheadModel = this.model = null;
      this.updateFilteredOptions(''); // reset to full list
      setTimeout(() => {
        this.onClear.emit(null);
      });
    } else {
      this.updateFilteredOptions(value);
    }
    if (this.editable && this.model !== value ) { //to execute when unknown values to be accepted
      this.model = value;
      setTimeout(() => {
        this.onSelect.emit(value);
      });
    }
  }

  getOptions(options: any, searchValue: string) {
    // transform the options only once
    if (!this.isOptionsTransformed) {
      //transforming the options so that it is always array of objects
      this.transformedOptions = this.disableSorting ? options : sort(this.transformOptionsList(options), this.propertyName, this.caseSensitive);
      this.isOptionsTransformed = true;
    }
    return typeof (searchValue) === 'string' ? StudioUtils.getFilteredOptions(this.transformedOptions, searchValue, this.groupBy, this.propertyName) : this.transformedOptions;
  }

  handleClear(event?: Event) {
    console.log('handle clear');
    event?.preventDefault();
    this.typeaheadModel = this.tempTypeaheadModel = this.model = null;
    this.toggleFocus(false);
    this.updateFilteredOptions(''); // reset to full list
    this.onTouched();
    setTimeout(() => {
      this.inputRef?.nativeElement.blur();
      this.onClear.emit(null);
    }, 0)
  }

  toggleFocus(isFocused: boolean) {
    if (isFocused) {
      this.expandBtnRef?.nativeElement.classList.add('ng-hide');
      this.clearBtnRef?.nativeElement.classList.remove('ng-hide');
    } else {
      this.clearBtnRef?.nativeElement.classList.add('ng-hide');
      this.expandBtnRef?.nativeElement.classList.remove('ng-hide');
    }
  }

  // highlight the characters in the dropdown matching the query.
  highlight(match: TypeaheadMatch, query: string) {
    this.typeaheadContainer = this.typeahead._container;
    if (this.typeaheadContainer && query) {
      return this.typeaheadContainer.highlight(match, query);
    }
    return;
  }
  
  handleCreateNew(): void {
    this.onCreateNew.emit(this.typeaheadModel?.trim()); // Emit new value
  }

  onItemIconClick(match: TypeaheadMatch): void {
    this.onTrailingIconClick.emit(match.item);
  }

}
